import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Fade,
  Grid,
  InputAdornment,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiService, ConnectorConfig } from '../../services/api';
import { useProjectContext } from '../../context/ProjectContext';
import { CatalogConnector, CatalogResponse } from '../../types/connectorCatalog';
import ConnectorCard from './ConnectorCard';
import ConnectorSetupDialog from './ConnectorSetupDialog';
import MyConnectorsTab from './MyConnectorsTab';

const ConnectorCatalogPage: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { selectedProjectId } = useProjectContext();

  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [directionFilter, setDirectionFilter] = useState<string | null>(null);
  const [setupConnector, setSetupConnector] = useState<CatalogConnector | null>(null);
  const [editingConnector, setEditingConnector] = useState<ConnectorConfig | null>(null);
  const [toast, setToast] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: '',
  });

  // Fetch catalog
  const catalogQuery = useQuery<CatalogResponse>(
    ['connector-catalog'],
    () => apiService.getConnectorCatalog(),
    { staleTime: 10 * 60 * 1000, refetchOnWindowFocus: false }
  );

  const catalog = catalogQuery.data?.connectors || [];
  const categories = catalogQuery.data?.categories || [];

  // Filtered & grouped catalog (for Catalog tab)
  const filteredCatalog = useMemo(() => {
    let items = catalog;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (c) =>
          c.display_name.toLowerCase().includes(q) ||
          c.short_description.toLowerCase().includes(q) ||
          c.tags.some((t) => t.includes(q))
      );
    }
    if (selectedCategory) {
      items = items.filter((c) => c.category === selectedCategory);
    }
    if (directionFilter) {
      items = items.filter((c) => c.direction === directionFilter || c.direction === 'both');
    }

    return items;
  }, [catalog, searchQuery, selectedCategory, directionFilter]);

  // Group by category (installed connectors first within each category)
  const groupedCatalog = useMemo(() => {
    const groups: Record<string, CatalogConnector[]> = {};
    for (const c of filteredCatalog) {
      if (!groups[c.category]) groups[c.category] = [];
      groups[c.category].push(c);
    }
    const categoryOrder = categories.map((c) => c.name);
    const sorted: Array<[string, CatalogConnector[]]> = [];
    for (const cat of categoryOrder) {
      if (groups[cat]) {
        const sortedItems = [...groups[cat]].sort((a, b) => {
          const aInstalled = a.installed !== false ? 1 : 0;
          const bInstalled = b.installed !== false ? 1 : 0;
          return bInstalled - aInstalled;
        });
        sorted.push([cat, sortedItems]);
      }
    }
    for (const [cat, items] of Object.entries(groups)) {
      if (!categoryOrder.includes(cat)) sorted.push([cat, items]);
    }
    return sorted;
  }, [filteredCatalog, categories]);

  // Create connector mutation
  const createMutation = useMutation(
    async (data: {
      connector_type: string;
      name: string;
      direction: 'source' | 'destination';
      config: Record<string, any>;
      project_id?: string;
    }) => {
      return apiService.createConnector(data);
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['connectors']);
        setSetupConnector(null);
        setToast({ open: true, severity: 'success', message: `${variables.name} connected successfully` });
      },
      onError: (err: any) => {
        const errorMessage = err.message || '';
        if (errorMessage.toLowerCase().includes('already exists')) {
          setToast({ open: true, severity: 'error', message: errorMessage });
        } else {
          setToast({ open: true, severity: 'error', message: `Failed to connect: ${errorMessage}` });
        }
      },
    }
  );

  // Update existing connector mutation
  const updateMutation = useMutation(
    async ({ connectorId, data }: { connectorId: string; data: { name: string; config: Record<string, any>; direction?: string; connector_type?: string } }) => {
      return apiService.updateConnector(connectorId, data);
    },
    {
      onSuccess: async (response, variables) => {
        await queryClient.invalidateQueries(['connectors']);
        setSetupConnector(null);
        setEditingConnector(null);
        const updatedName = response?.name ?? variables.data.name;
        setToast({ open: true, severity: 'success', message: `${updatedName} updated successfully` });
      },
      onError: (err: any) => {
        const errorMessage = err.message || '';
        if (errorMessage.toLowerCase().includes('already exists')) {
          setToast({ open: true, severity: 'error', message: errorMessage });
        } else {
          setToast({ open: true, severity: 'error', message: `Failed to update: ${errorMessage}` });
        }
      },
    }
  );

  const handleConnectorClick = useCallback((connector: CatalogConnector) => {
    setSetupConnector(connector);
    setEditingConnector(null);
  }, []);

  const handleSave = useCallback(
    (data: {
      connector_type: string;
      name: string;
      direction: 'source' | 'destination';
      config: Record<string, any>;
      catalog_connector_id: string;
      project_id?: string;
    }) => {
      createMutation.mutate({
        connector_type: data.connector_type,
        name: data.name,
        direction: data.direction,
        config: data.config,
        project_id: data.project_id,
      });
    },
    [createMutation]
  );

  const handleUpdate = useCallback(
    (connectorId: string, data: { name: string; config: Record<string, any>; direction?: string; connector_type?: string }) => {
      updateMutation.mutate({ connectorId, data });
    },
    [updateMutation]
  );

  const handleEditConnector = useCallback(
    (catalogEntry: CatalogConnector, connector: ConnectorConfig) => {
      setSetupConnector(catalogEntry);
      setEditingConnector(connector);
    },
    []
  );

  if (catalogQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalCount = catalog.length;

  return (
    <Box>
      {/* Header */}
      <Fade in timeout={300}>
        <Box
          sx={{
            mb: 3,
            pb: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            borderTop: '3px solid',
            borderImage: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main}) 1`,
            pt: 3,
            px: 0.5,
          }}
        >
          <Typography variant="h4" fontWeight={700}>
            Connections
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage your connectors and browse {totalCount}+ data sources and destinations
          </Typography>
        </Box>
      </Fade>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="My Connectors" />
        <Tab label="Catalog" />
      </Tabs>

      {/* Tab 0: My Connectors */}
      {activeTab === 0 && (
        <MyConnectorsTab
          catalog={catalog}
          onEditConnector={handleEditConnector}
        />
      )}

      {/* Tab 1: Catalog */}
      {activeTab === 1 && (
        <Box>
          {/* Search & Filters */}
          <Fade in timeout={500}>
            <Box sx={{ mb: 3 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search connectors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ minWidth: 300 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip
                    label="All"
                    size="small"
                    variant={!selectedCategory ? 'filled' : 'outlined'}
                    color={!selectedCategory ? 'primary' : 'default'}
                    onClick={() => setSelectedCategory(null)}
                    sx={{ fontWeight: !selectedCategory ? 600 : 400 }}
                  />
                  {categories.map((cat) => (
                    <Chip
                      key={cat.name}
                      label={`${cat.name} (${cat.count})`}
                      size="small"
                      variant={selectedCategory === cat.name ? 'filled' : 'outlined'}
                      color={selectedCategory === cat.name ? 'primary' : 'default'}
                      onClick={() => setSelectedCategory(selectedCategory === cat.name ? null : cat.name)}
                      sx={{ fontWeight: selectedCategory === cat.name ? 600 : 400 }}
                    />
                  ))}
                </Stack>
              </Stack>

              {/* Direction filter */}
              <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }}>
                <Chip
                  label="All Directions"
                  size="small"
                  variant={!directionFilter ? 'filled' : 'outlined'}
                  color={!directionFilter ? 'primary' : 'default'}
                  onClick={() => setDirectionFilter(null)}
                  sx={{ fontWeight: !directionFilter ? 600 : 400, fontSize: '0.75rem' }}
                />
                <Chip
                  label="Sources"
                  size="small"
                  variant={directionFilter === 'source' ? 'filled' : 'outlined'}
                  color={directionFilter === 'source' ? 'primary' : 'default'}
                  onClick={() => setDirectionFilter(directionFilter === 'source' ? null : 'source')}
                  sx={{ fontWeight: directionFilter === 'source' ? 600 : 400, fontSize: '0.75rem' }}
                />
                <Chip
                  label="Destinations"
                  size="small"
                  variant={directionFilter === 'destination' ? 'filled' : 'outlined'}
                  color={directionFilter === 'destination' ? 'primary' : 'default'}
                  onClick={() => setDirectionFilter(directionFilter === 'destination' ? null : 'destination')}
                  sx={{ fontWeight: directionFilter === 'destination' ? 600 : 400, fontSize: '0.75rem' }}
                />
              </Stack>
            </Box>
          </Fade>

          {/* Catalog Grid - Grouped by category */}
          {groupedCatalog.map(([category, connectors]) => (
            <Fade in timeout={600} key={category}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  {category}
                </Typography>
                <Grid container spacing={2}>
                  {connectors.map((connector) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={connector.connector_id}>
                      <ConnectorCard
                        connector={connector}
                        onClick={handleConnectorClick}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Fade>
          ))}

          {filteredCatalog.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="body1" color="text.secondary">
                No connectors match your search.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Setup Dialog — shared across tabs */}
      <ConnectorSetupDialog
        open={!!setupConnector}
        connector={setupConnector}
        existingConnector={editingConnector}
        onClose={() => { setSetupConnector(null); setEditingConnector(null); }}
        onSave={handleSave}
        onUpdate={handleUpdate}
        projectId={selectedProjectId ?? undefined}
      />

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConnectorCatalogPage;
